import { Token, Brand, ParkId, FeatureFlagUser, ClientPrivateKeyPem, Drift } from '@/types';

import { StorageService } from './storage';

enum PersistentKeys {
  Token = 'token',
  Drift = 'driftInSeconds',
  ClientPrivateKeyPem = 'clientPrivateKeyPem',
  Brand = 'brand',
  ParkId = 'parkId',
  FeatureFlagUser = 'featureFlagUser',
  OnHoldPassesAlertDismissed = 'onHoldPassesAlertDismissed',
  IsInPark = 'isInPark',
  Lat = 'lat',
  Lng = 'lng',
}

class PersistentStorage {
  private readonly backingStore: StorageService<PersistentKeys>;

  constructor() {
    this.backingStore = new StorageService(window.localStorage);
  }

  public getToken(): Token | null {
    return this.backingStore.getItem<Token>(PersistentKeys.Token);
  }

  public setToken(token: Token): void {
    this.backingStore.setItem(PersistentKeys.Token, token);
  }

  public getClientPrivateKeyPem(): ClientPrivateKeyPem | null {
    return this.backingStore.getItem<ClientPrivateKeyPem>(PersistentKeys.ClientPrivateKeyPem);
  }

  public setClientPrivateKey(clientPrivateKeyPem: ClientPrivateKeyPem): void {
    this.backingStore.setItem(PersistentKeys.ClientPrivateKeyPem, clientPrivateKeyPem);
  }

  public getDriftInSeconds(): Drift | null {
    return this.backingStore.getItem<Drift>(PersistentKeys.Drift);
  }

  public setDriftInSeconds(driftInSeconds: Drift): void {
    this.backingStore.setItem(PersistentKeys.Drift, driftInSeconds);
  }

  public getBrand(): Brand | null {
    return this.backingStore.getItem<Brand>(PersistentKeys.Brand);
  }

  public setBrand(brand: Brand): void {
    this.backingStore.setItem(PersistentKeys.Brand, brand);
  }

  public getParkId(): ParkId | null {
    return this.backingStore.getItem<ParkId>(PersistentKeys.ParkId);
  }

  public setParkId(parkId: ParkId): void {
    this.backingStore.setItem(PersistentKeys.ParkId, parkId);
  }

  public getFeatureFlagUser(): FeatureFlagUser | null {
    return this.backingStore.getItem<FeatureFlagUser>(PersistentKeys.FeatureFlagUser);
  }

  public setFeatureFlagUser(user: FeatureFlagUser): void {
    this.backingStore.setItem(PersistentKeys.FeatureFlagUser, user);
  }

  public getIsInPark(): boolean | null {
    return this.backingStore.getItem<boolean>(PersistentKeys.IsInPark);
  }

  public setIsInPark(isInPark: boolean): void {
    this.backingStore.setItem(PersistentKeys.IsInPark, isInPark);
  }

  public getLat(): number | null {
    return this.backingStore.getItem<number>(PersistentKeys.Lat);
  }

  public setLat(lat: number): void {
    this.backingStore.setItem(PersistentKeys.Lat, lat);
  }

  public getLng(): number | null {
    return this.backingStore.getItem<number>(PersistentKeys.Lng);
  }

  public setLng(lng: number): void {
    this.backingStore.setItem(PersistentKeys.Lng, lng);
  }
}

export const PersistentStorageService = new PersistentStorage();

// Development/testing helpers
window.setToken = (token: Token) => PersistentStorageService.setToken(token);
window.setClientPrivateKey = (clientPrivateKeyPem: ClientPrivateKeyPem) =>
  PersistentStorageService.setClientPrivateKey(clientPrivateKeyPem);
window.setDriftInSeconds = (driftInSeconds: Drift) =>
  PersistentStorageService.setDriftInSeconds(driftInSeconds);
window.setBrand = (brand: Brand) => PersistentStorageService.setBrand(brand);
window.setParkId = (parkId: ParkId) => PersistentStorageService.setParkId(parkId);
window.setFeatureFlagUser = (user: FeatureFlagUser) =>
  PersistentStorageService.setFeatureFlagUser(user);
(window as any).setIsInPark = (isInPark: boolean) => PersistentStorageService.setIsInPark(isInPark);
(window as any).setLat = (lat: number) => PersistentStorageService.setLat(lat);
(window as any).setLng = (lng: number) => PersistentStorageService.setLng(lng);
